import { MfeReactComponent } from '@carvana/microfrontend-react';
import React, { useEffect, useRef } from 'react';
import { EventedModal, ModalNames } from '|/app/modals';
import { useIsMobileViewport } from '|/hooks/use-is-mobile-viewport';

const SUPPORT_DROPDOWN_CLOSE_EVENT = 'header|support-dropdown|close';

/**
 * ds-modal 6.1 computes `zIndex` as `1300 + depth * 100` unless one is passed. 700 clears
 * the header and nav rail, which is all this modal needed while it only ever opened from
 * them — but it sits *behind* any open modal. Callers that open support from inside another
 * modal must raise it above that modal's own z-index.
 */
const DEFAULT_SUPPORT_Z_INDEX = 700;

interface SupportMFEModalProps {
  isOpen: boolean;
  onClose: () => void;
  closeMobileNavMenu?: () => void;
  zIndex?: number;
}

const SupportMFEModal: React.FC<SupportMFEModalProps> = ({
  isOpen,
  onClose,
  closeMobileNavMenu = () => {
    /* empty */
  },
  zIndex = DEFAULT_SUPPORT_Z_INDEX
}) => {
  const eventElement = useRef<HTMLDivElement>(null);
  const isMobileViewport = useIsMobileViewport();

  useEffect(() => {
    let eventElemRef: HTMLDivElement | null = null;

    const handler = (event: Event) => {
      event.preventDefault();
      closeMobileNavMenu();
      onClose();
    };

    if (eventElement.current) {
      eventElemRef = eventElement.current;
      eventElement.current.addEventListener(SUPPORT_DROPDOWN_CLOSE_EVENT, handler);
    }

    return () => {
      if (eventElemRef) {
        eventElemRef.removeEventListener(SUPPORT_DROPDOWN_CLOSE_EVENT, handler);
      }
    };
  }, [onClose, closeMobileNavMenu]);

  const handleClose = () => {
    closeMobileNavMenu();
    onClose();
  };

  const modalStyleProps = isMobileViewport
    ? ({ mobileStyle: 'full' } as const)
    : ({ drawerStyle: 'all', drawerSide: 'right', drawerWidth: '400px' } as const);

  return (
    <EventedModal
      isOpen={isOpen}
      analyticsId={ModalNames.SupportContact}
      onClose={handleClose}
      modalHeader="Support & Contact"
      {...modalStyleProps}
      closeIcon={true}
      disableOutsideClose={false}
      disableEscapeClose={false}
      contentPadding={false}
      zIndex={zIndex}
    >
      <div id="account-menu-scroll-target" />
      <div ref={eventElement} id="header-plugin-events" />
      <MfeReactComponent
        module="CoreSupportDropdown"
        component="default"
        withModal={false}
        isOpen={isOpen}
      />
    </EventedModal>
  );
};

export default SupportMFEModal;
